import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { apiService } from '../services/apiService';

const actionTypes = [
  '',
  'LOGIN',
  'LOGOUT',
  'ROLE_CHANGE',
  'ADD_TEAM_MEMBER',
  'REMOVE_TEAM_MEMBER',
  'CREATE_PROJECT',
  'UPDATE_PROJECT',
  'DELETE_PROJECT',
  'RESTORE_PROJECT',
  'PERMANENT_DELETE_PROJECT',
  'CREATE_ENVIRONMENT',
  'UPDATE_ENVIRONMENT',
  'DELETE_ENVIRONMENT',
  'RESTORE_ENVIRONMENT',
  'PERMANENT_DELETE_ENVIRONMENT',
  'DOWNLOAD_PROXY',
  'ADMIN_API_ACCESS',
];

function safeJsonParse(value: any): any | null {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function truncate(value: string, max = 120) {
  if (!value) return value;
  if (value.length <= max) return value;
  return value.slice(0, max) + '…';
}

const SuperAdminAuditLogsPage: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [actionType, setActionType] = useState('');
  const [userId, setUserId] = useState('');

  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setDebouncedSearch(search), 350);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [search]);

  const query = useMemo(() => {
    const uid = userId.trim() ? Number(userId.trim()) : undefined;
    return {
      page: 0,
      size: 80,
      search: debouncedSearch || undefined,
      actionType: actionType || undefined,
      userId: Number.isFinite(uid as any) ? uid : undefined,
      sortBy: 'createdAt',
      sortDir: 'desc',
    };
  }, [debouncedSearch, actionType, userId]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getAdminAuditLogs(query);
      setRows(res?.content || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [query]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Activity Tracking (Audit Logs)
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          size="small"
          label="Global Search (email / metadata / entity id)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <TextField
          size="small"
          label="User ID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
        <TextField
          size="small"
          select
          label="Action"
          value={actionType}
          onChange={(e) => setActionType(e.target.value)}
        >
          {actionTypes.map((a) => (
            <MenuItem key={a || 'all'} value={a}>
              {a || 'All'}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={() => apiService.exportAdminAuditCsv(query)}>
          Export CSV
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setSearch('');
            setActionType('');
            setUserId('');
          }}
        >
          Clear
        </Button>
      </Box>

      {loading && <CircularProgress size={22} />}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Who</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>Before</TableCell>
              <TableCell>After</TableCell>
              <TableCell>Metadata</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const metaJson = safeJsonParse(row.metadataText);
              const before = metaJson?.before;
              const after = metaJson?.after;
              const metaShort = truncate(row.metadataText || '', 160);

              return (
                <TableRow key={row.id}>
                  <TableCell>{row.createdAt || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{row.userEmail || '-'}</span>
                      <span style={{ opacity: 0.7, fontSize: 12 }}>#{row.userId ?? '-'}</span>
                    </Box>
                  </TableCell>
                  <TableCell>{row.actionType}</TableCell>
                  <TableCell>
                    {row.entityType} {row.entityId ? `#${row.entityId}` : ''}
                  </TableCell>
                  <TableCell>{row.ipAddress || '-'}</TableCell>
                  <TableCell>
                    {(row.osName || '-') + ' / ' + (row.browserName || '-') + (row.deviceType ? ` / ${row.deviceType}` : '')}
                  </TableCell>
                  <TableCell>
                    {before ? (
                      <Tooltip title={<pre style={{ margin: 0 }}>{JSON.stringify(before, null, 2)}</pre>}>
                        <span style={{ cursor: 'help' }}>{truncate(JSON.stringify(before), 80)}</span>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {after ? (
                      <Tooltip title={<pre style={{ margin: 0 }}>{JSON.stringify(after, null, 2)}</pre>}>
                        <span style={{ cursor: 'help' }}>{truncate(JSON.stringify(after), 80)}</span>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {row.metadataText ? (
                      <Tooltip title={<pre style={{ margin: 0, maxWidth: 700, whiteSpace: 'pre-wrap' }}>{row.metadataText}</pre>}>
                        <span style={{ cursor: 'help' }}>{metaShort}</span>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default SuperAdminAuditLogsPage;

